import { inject, Injectable } from '@angular/core';
import { Action, createSelector, State, StateContext } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';

import { Page } from '../../../../shared/model/local/pagination/page';
import { BiomarkerActions } from './biomarker.actions';
import { BiomarkerApiService } from '../service/biomarker.api.service';
import { Biomarker } from '../model/biomarker';
import { BiomarkerPageCriteria } from '../model/biomarker-page-criteria';
import { BiomarkerSignalModel } from '../model/biomarker-signal';
import { SignalPageCriteria } from '../model/signal-page-criteria';

export interface BiomarkersStateModel {
  page: Page<Biomarker>;
  pageCriteria: BiomarkerPageCriteria;
  signalsPage: Page<BiomarkerSignalModel>;
  signalsPageCriteria: SignalPageCriteria;
}

@State<BiomarkersStateModel>({
  name: 'biomarker',
  defaults: {
    page: null,
    pageCriteria: null,
    signalsPage: null,
    signalsPageCriteria: null,
  },
})
@Injectable()
export class BiomarkersState {
  private readonly biomarkerApiService: BiomarkerApiService =
    inject(BiomarkerApiService);

  static page() {
    return createSelector([BiomarkersState], (state: BiomarkersStateModel) => {
      return state.page;
    });
  }

  static pageCriteria() {
    return createSelector([BiomarkersState], (state: BiomarkersStateModel) => {
      return state.pageCriteria;
    });
  }

  static signalsPage() {
    return createSelector([BiomarkersState], (state: BiomarkersStateModel) => {
      return state.signalsPage;
    });
  }

  static signalsPageCriteria() {
    return createSelector([BiomarkersState], (state: BiomarkersStateModel) => {
      return state.signalsPageCriteria;
    });
  }

  @Action(BiomarkerActions.GetPage, { cancelUncompleted: true })
  async getPage(
    ctx: StateContext<BiomarkersStateModel>,
    action: BiomarkerActions.GetPage
  ) {
    try {
      const page = await firstValueFrom(
        this.biomarkerApiService.getBiomarkers(action.criteria)
      );

      ctx.patchState({
        page,
        pageCriteria: action.criteria,
      });

      return Promise.resolve();
    } catch (ex) {
      return Promise.reject(ex);
    }
  }

  @Action(BiomarkerActions.GetSignalsPage, { cancelUncompleted: true })
  async getSignalsPage(
    ctx: StateContext<BiomarkersStateModel>,
    action: BiomarkerActions.GetSignalsPage
  ) {
    try {
      const signalsPage = await firstValueFrom(
        this.biomarkerApiService.getSignals(action.criteria)
      );

      ctx.patchState({
        signalsPage,
        signalsPageCriteria: action.criteria,
      });

      return Promise.resolve();
    } catch (ex) {
      return Promise.reject(ex);
    }
  }
}
